import * as React from "react";
import {
  ReferenceInput,
  SelectInput,
  TextInput,
  useRecordContext,
  useGetOne,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  NumberInput,
  useUpdate,
  useCreate,
  SaveButton,
  DeleteButton,
  Toolbar,
  AutocompleteInput,
  DateTimeInput,
  required,
  useNotify
} from "react-admin";

import { CircularProgress, Grid } from "@mui/material";
import Transformer from "../../common/Transformer";
import {
  StatusEnum,
  StockStatusEnum,
  ProductTypeEnum,
  ProductSupplierEnum
} from "../../common/enum";
import { ProductEditComponent } from "./ProductComponent";
import { PriceForm } from "./PriceForm";
import {
  attributesToCamilCase,
  clearEmptyAttributes,
  clearEmptyNaNAttributes,
} from "src/common/UtilFunctions";
import { GalleryTab } from "./GalleryTab";
import { Edit, FormTab, TabbedForm, IfCanAccess } from "src/enterprise/ra-rbac";

const CustomField = (props) => {
  const attribute = props.attribute;
  let component = <p>Loading...</p>;

  switch (attribute.type) {
    case "select": {
      const choices = attribute.options.map((option) => ({
        id: option.value,
        name: option.value,
      }));
      component = (
        <AutocompleteInput
          source={props.source}
          label={attribute.name}
          choices={choices}
          defaultValue=""
          size="small"
        />
      );
      break;
    }
    case "multiselect": {
      const choices = attribute.options.map((option) => ({
        id: option.value,
        name: option.value,
      }));
      component = (
        <AutocompleteArrayInput
          source={props.source}
          label={attribute.name}
          size="small"
          choices={choices}
        />
      );
      break;
    }
    case "datetime": {
      component = (
        <DateTimeInput
          source={props.source}
          label={attribute.name}
        />
      );
      break;
    }
    case "number": {
      component = (
        <NumberInput
          source={props.source}
          label={attribute.name}
        />
      );
      break;
    }
    default: {
      component = (
        <TextInput
          source={props.source}
          label={attribute.name}
        />
      );
      break;
    }
  }

  return component;
};

const ProductEditToolbar = () => {
  return (
    <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
      <SaveButton alwaysEnable />
      <IfCanAccess action="delete">
        <DeleteButton />
      </IfCanAccess>
    </Toolbar>
  );
};

const ProductEditForm = (props) => {
  const record = useRecordContext(props);
  const setGalleryItems = props.setGalleryItems;
  let attributeId = record?.attributeSet
    ? record.attributeSet
    : record?.attribute_set.id;
  const {
    data: attributeSetData,
    isLoading,
    error,
  } = useGetOne("admin/product/attribute-sets", { id: attributeId });
  const filterToQuery = (searchText, recordId) => ({
    "search": searchText,
    "id.not": recordId,
  });
  const notify = useNotify();

  if (isLoading || error) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <CircularProgress size={40} />
      </div>
    );
  }
  

  return (
    <>
      <TabbedForm sanitizeEmptyValues toolbar={<ProductEditToolbar />}>
        <FormTab label="General" name="general">
          <Grid container rowSpacing={1} columnSpacing={2}>
            <Grid item sm={6} md={3}>
              <TextInput disabled source="id"/>
            </Grid>
            <Grid item sm={6} md={3}>
              <TextInput disabled source="attribute_set.name" label="Attribute Set" />
            </Grid>
            <Grid container item rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12} md={6}>
                <TextInput source="name" validate={required()}/>
              </Grid>
              <Grid item sm={12} md={6}>
                <ReferenceInput
                  source="parent.id"
                  reference="admin/products"
                  filter={{ parent: null, "id.not": record?.id }}
                  perPage={50}
                >
                  <AutocompleteInput
                    filterToQuery={(e) => filterToQuery(e, record?.id)}
                    optionText={(record) => record.sku + " " + record.name}
                    size="small"
                    label="Parent"
                    defaultValue=""
                  />
                </ReferenceInput>
              </Grid>
            </Grid>
            <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
              <Grid item sm={12}> 
                <TextInput
                  source="description"
                  sx={{
                    textarea: { minHeight: "215px" }
                  }}
                  fullWidth
                  multiline
                  validate={required()}
                />
              </Grid>
              <Grid item sm={12}> 
                <TextInput
                  source="short_description"
                  sx={{
                    textarea: { minHeight: "87.5px" }
                  }}
                  label="Short Description"
                  fullWidth
                  multiline
                  validate={required()}
                />
              </Grid>   
            </Grid>
            <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
                <Grid item sm={12}>
                  <TextInput source="slug" validate={required()} />
                </Grid>
                <Grid item sm={12}>
                  <TextInput source="sku" validate={required()} />
                </Grid>
                <Grid item sm={12}>
                  <SelectInput source="supplier" choices={ProductSupplierEnum} validate={required()} />
                </Grid>
                <Grid item sm={12}>
                  <TextInput source="supplier_sku" label="Supplier Sku"/>
                </Grid>
                <Grid item sm={12}>
                  <SelectInput source="type" choices={ProductTypeEnum} validate={required()} />
                </Grid>
                <Grid item sm={12}>
                  <NumberInput source="weight" min={0} validate={required()} />
                </Grid>
                <Grid item sm={12}>
                  <SelectInput source="status" choices={StatusEnum} validate={required()} />
                </Grid>
                <Grid item sm={12}> 
                  <SelectInput
                    source="stock_status"
                    label="Stock Status"
                    choices={StockStatusEnum}
                    validate={required()}
                  />
                </Grid>
                <Grid item sm={12}>
                  <TextInput source="mill_name" />
                </Grid>
            </Grid>
          </Grid>
        </FormTab>
        <FormTab label="SEO" name="seo">
          <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
            <Grid item sm={12}>
              <TextInput source="meta_title"/>
            </Grid>
            <Grid item sm={12}>
              <TextInput
                source="meta_description"
                sx={{
                  textarea: { minHeight: "87.5px" }
                }}
                fullWidth
                multiline
              />
            </Grid>
            <Grid item sm={12}>
              <TextInput source="meta_keywords"/>
            </Grid>
          </Grid>
        </FormTab>
        <FormTab label="Categories" name="categories">
          <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
            <Grid item sm={12}>
              <ReferenceArrayInput
                source="category_ids"
                reference="admin/product/categories"
                children={
                  <AutocompleteArrayInput
                    optionText="name"
                    label="Categories"
                    size="small"
                    sx={{ minWidth: "35vw" }}
                  />
                }
              />
            </Grid>
          </Grid>
        </FormTab>
        <FormTab label="Related Products" name="related_products">
          <Grid container item sm={12} md={6} rowSpacing={1} columnSpacing={2}>
            <Grid item sm={12}>
              <ReferenceArrayInput
                source="related_product_ids"
                reference="admin/products"
              >
                <AutocompleteArrayInput
                  filterToQuery={(e) => filterToQuery(e, record?.id)}
                  size="small"
                  optionText={(record) => record.sku + " " + record.name}
                  sx={{ minWidth: "35vw" }}
                />
              </ReferenceArrayInput>
            </Grid>
          </Grid>
        </FormTab>
        <FormTab label="Attributes" name="attributes">
          <Grid container item sm={12} rowSpacing={1} columnSpacing={2}>
            {attributeSetData &&
              attributeSetData?.attributes.map((att, key) => (
                <Grid item sm={12} md={6} key={key}>
                  <CustomField source={"attributes."+att.code} attribute={att} key={key} />
                </Grid>
              ))}
          </Grid>
        </FormTab>
        <FormTab label="Prices" name="prices">
          <PriceForm />
        </FormTab>
        <FormTab label="Gallery" name="gallery">
          <GalleryTab setGalleryItems={setGalleryItems} />
        </FormTab>
      </TabbedForm>
    </>
  );
};

export const ProductEdit = (props) => {
  const [create, { isLoading: createLoading, error: createError }] =
    useCreate();
  const [update, { data, isLoading: updateLoading, error: updateError }] =
    useUpdate();
  const [galleryItems, setGalleryItems] = React.useState([]);

  const transform = (data, { previousData }) => {
    let productPrice = data?.price;

    Transformer(data, {
      remove: [
        "id",
        "createdAt",
        "createdBy",
        "updatedAt",
        "updatedBy",
        "attributeSet",
        "sampleQuantity",
        "categoryIds",
        "relatedProductIds",
        "visibility",
        "children",
        "price",
        "galleryFeatured",
        "gallery",
        "stockQuantity",
        "stock"
      ],
      update: {
        weight: parseFloat(data.weight),
        parent: data.parent.id,
        categories: data["category_ids"],
        relatedProducts: data["related_product_ids"],
      },
    });

    if (productPrice) {
      Transformer(productPrice, {
        remove: [
          "id",
          "createdAt",
          "createdBy",
          "updatedAt",
          "updatedBy",
          "tax_class",
        ],
        update: {
          taxClass: productPrice.tax_class.id,
          product: previousData.id,
        },
      });

      if (previousData.price) updatePrice(productPrice, previousData);
      else createPrice(productPrice);
    }

    if (galleryItems.length > 0) {
      updateGallery(galleryItems);
    }

    clearEmptyAttributes(data.attributes);
    clearEmptyNaNAttributes(data);

    return data;
  };

  const updatePrice = (productPrice, previousData) => {
    attributesToCamilCase(productPrice);
    clearEmptyNaNAttributes(productPrice);
    if (!updateLoading && !updateError)
      update("admin/product/prices", {
        id: previousData.price.id,
        data: productPrice,
        previousData: previousData.price,
      });
  };

  const createPrice = (productPrice) => {
    attributesToCamilCase(productPrice);
    clearEmptyNaNAttributes(productPrice);
    if (!createLoading && !createError)
      create("admin/product/prices", { data: productPrice });
  };

  const updateGallery = (galleryItems) => {
    galleryItems.forEach((item) => {
      let galleryItemId = item.id;
      Transformer(item, {
        remove: ["id", "createdAt", "createdBy", "updatedAt", "updatedBy"],
        update: {
          product: item.product?.id,
          sortOrder: item["sort_order"],
          featured: item.featured.toString(),
        },
      });
      update("admin/product/gallery", { id: galleryItemId, data: item });
    });
  };

  return (
    <Edit
      component={ProductEditComponent}
      transform={transform}
      mutationMode="pessimistic"
      {...props}
    >
      <ProductEditForm galleryItems={galleryItems} setGalleryItems={setGalleryItems} />
    </Edit>
  );
};
